import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Spinner,
  Container,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import useUserAgreements from "hooks/useUserAgreements";
import useFormattedDate from "hooks/useFormatDate";

const UserAgreementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAgreementById } = useUserAgreements();

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { formatDateTime} =useFormattedDate();

  useEffect(() => {
    if (!id) return;

    const loadAgreement = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAgreementById(id);
        setAgreement(data);
      } catch (err) {
        setError(err.message || "Failed to load agreement details");
        toast.error("Failed to load agreement details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadAgreement();
  }, [id, getAgreementById]);

  if (loading) {
    return (
      <Container className="my-5">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 400 }}
        >
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-sm text-primary">
            Loading agreement details...
          </span>
        </div>
      </Container>
    );
  }

  if (error || !agreement) {
    return (
      <Container className="my-5">
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            size="3x"
            className="text-warning mb-3"
          />
          <h4>Agreement Not Found</h4>
          <p className="text-muted">
            {error || "The requested agreement could not be found."}
          </p>
          <Button
            variant="outline-primary"
            onClick={() => navigate("/general/user-agreements")}
          >
            Back
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="my-4 px-2 px-md-0">
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-2">
        <Button
          type="button"
          onClick={() => navigate("/general/user-agreements")}
          size="sm"
          aria-label="Go back"
          variant="outline-success"
          className="d-flex align-items-center w-auto"
        >
          <FontAwesomeIcon icon="arrow-left" className="me-2" />
          <span className="d-none d-sm-inline">Back</span>
        </Button>

        <div className="d-flex gap-2 w-100 w-sm-auto justify-content-end">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate(`/general/user-agreements/${id}/edit`)}
            className="flex-fill flex-sm-auto"
          >
            <FontAwesomeIcon icon="edit" className="me-2" />
            <span className="d-none d-md-inline">Edit Agreement</span>
            <span className="d-md-none">Edit</span>
          </Button>
        </div>
      </div>

      <WidgetSectionTitle
        className="mb-4"
        title={`User Agreement - ${agreement.version}`}
        icon="file-contract"
        subtitle="Review the complete terms and conditions of this user agreement"
      />

      <Row className="g-3">
        <Col xs={12} lg={8} className="order-2 order-lg-1">
          <Card className="shadow-sm">
            <Card.Header className="bg-light d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
              <h5 className="mb-0 fs-6 fs-md-5">Agreement Content</h5>
              <Badge
                bg={agreement.isActive !== false ? "success" : "secondary"}
                className="align-self-start align-self-sm-center"
              >
                {agreement.isActive !== false ? "Active" : "Inactive"}
              </Badge>
            </Card.Header>
            <Card.Body className="p-3 p-md-4">
              <div
                className="agreement-content"
                dangerouslySetInnerHTML={{ __html: agreement.details }}
                style={{
                  lineHeight: 1.6,
                  fontSize: "0.875rem",
                  color: "#495057",
                }}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4} className="order-1 order-lg-2">
          <Card className="shadow-sm mb-3 mb-lg-4">
            <Card.Header className="bg-light">
              <h6 className="mb-0 fs-6">Agreement Information</h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="mb-3">
                <strong className="d-block mb-1">Version:</strong>
                <div className="text-break">{agreement.version}</div>
              </div>
              <div className="mb-3">
                <strong className="d-block mb-1">Status:</strong>
                <div>
                  <Badge
                    bg={agreement.isActive !== false ? "success" : "secondary"}
                  >
                    {agreement.isActive !== false ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
              <div className="mb-3">
                <strong className="d-block mb-1">Created:</strong>
                <div className="small">
                  {formatDateTime(agreement.createdAt)}
                </div>
              </div>
              <div className="mb-3">
                <strong className="d-block mb-1">Last Updated:</strong>
                <div className="small">
                  {formatDateTime(agreement.updatedAt)}
                </div>
              </div>
              <div className="mb-3">
                <strong className="d-block mb-1">Created By:</strong>
                <div className="text-break small">{agreement.createdBy}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserAgreementDetails;
